import React from "react";
import ManualContent from "../../components/ManualContent";
import { MathJax } from "better-react-mathjax";
import { Link } from "react-router-dom";

const ModelList = () => {
  return (
    <ManualContent title="Available Experimental Models in Speqqle">
      <div id="top" />
      <p>
        Speqqle includes a library of <strong>experimental models</strong> that
        simulate and fit real-world optical measurements. These models are
        composed of one or more <strong>submodels</strong> (such as materials or
        layers) and define how your experiment interacts with those components.
      </p>

      <p>This section contains:</p>
      <ul>
        <li>
          A list of all <strong>available experimental models</strong>, each
          linked to a detailed explanation below.
        </li>
        <li>
          A dedicated section for each model, including:
          <ul>
            <li>What the model does and how it works</li>
            <li>Inputs and outputs</li>
            <li>When and why to use it</li>
            <li>Common mistakes to avoid</li>
            <li>A deep-dive into the underlying physics</li>
          </ul>
        </li>
      </ul>

      <h2>List of Experiments</h2>
      <ul>
        <li>
          <a href="#rtamodel">
            {" "}
            &quot;R/T/A/Ellips&quot;: Reflectivity / Transmission / Absorption /
            Ellipsometry{" "}
          </a>
        </li>
        <li>
          <a href="#ramanmodel">Raman (thermometry)</a>
        </li>
        <li>
          <a href="#PDmodelbulk">SNOM: the point dipole model for bulk samples</a>
        </li>
        <li>
          <a href="#FDmodelbulk">SNOM: the finite dipole model for bulk samples</a>
        </li>
        <li>
          <a href="#THzmodel">
            Coming soon: TeraHertz time domain spectroscopy
          </a>
        </li>
        <li>
          <a href="#Gasmodel">Coming soon: Gas absorption spectroscopy</a>
        </li>
        <li>
          <a href="#PDmodel">Coming soon: SNOM: the point dipole model for multilayer samples and thin films</a>
        </li>
        <li>
          <a href="#FDmodel">Coming soon: SNOM: the finite dipole model for multilayer samples and thin films</a>
        </li>
      </ul>

      {/* R/T/A/Ellips Section */}
      <MathJax>
        <section
          id="rtamodel"
          style={{ marginTop: "3rem", marginBottom: "3rem" }}
        >
          <h3>
            Reflectivity / Transmission / Absorption / Ellipsometry
            (R/T/A/Ellips)
          </h3>

          {/* Model Description */}
          <p>
            This model simulates and fits light–matter interactions through thin
            films, multilayers, or bulk materials, predicting optical responses
            such as <strong>Reflectivity</strong>, <strong>Transmission</strong>
            , <strong>Absorption</strong>, and <strong>Ellipsometry</strong>. It
            is ideal for extracting material properties and layer thicknesses in
            layered structures.
          </p>

          {/* Inputs Table */}
          <h4>Inputs</h4>
          <table className="manual-table">
            <thead>
              <tr>
                <th>Parameter</th>
                <th>Description</th>
                <th>Units</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td> Material Optical Response </td>
                <td>
                  Each layer must reference a <strong>Material submodel</strong>{" "}
                  providing the complex dielectric function{" "}
                  {"\\( \\varepsilon(\\omega) \\)"}.{" "}
                </td>
                <td>unitless</td>
              </tr>
              <tr>
                <td>Layer Thickness</td>
                <td>
                  Physical thickness of each material layer, including substrate
                </td>
                <td>cm</td>
              </tr>
              <tr>
                <td>Coherence (&quot;coh&quot;)</td>
                <td>
                  Determines whether <strong>interference effects</strong>{" "}
                  (e.g., Fabry-Pérot) are modeled in a given layer.
                </td>
                <td>1 = coherent, 0 = incoherent</td>
              </tr>
              <tr>
                <td>Angle of Incidence {"\\( \\theta \\)"}</td>
                <td>Incident angle of the light beam on the sample surface</td>
                <td>degrees</td>
              </tr>
              <tr>
                <td>Polarizer Angle (&quot;pol&quot;)</td>
                <td>Angle of linear polarization</td>
                <td>degrees</td>
              </tr>
            </tbody>
          </table>

          {/* Outputs Table */}
          <h4>Outputs</h4>
          <table className="manual-table">
            <thead>
              <tr>
                <th>Output</th>
                <th>Description</th>
                <th>Units</th>
                <th>Mode</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{"\\( R(\\omega) \\)"}</td>
                <td>
                  Reflectivity: power ratio of reflected to incident light
                </td>
                <td>unitless (0–1)</td>
                <td>Reflection</td>
              </tr>
              <tr>
                <td>{"\\( T(\\omega) \\)"}</td>
                <td>
                  Transmittivity: power ratio of transmitted to incident light
                </td>
                <td>unitless (0–1)</td>
                <td>Transmission</td>
              </tr>
              <tr>
                <td>{"\\( A(\\omega) \\)"}</td>
                <td>Absorption, computed as {"\\( A = 1 - R - T \\)"}</td>
                <td>unitless (0–1)</td>
                <td>All</td>
              </tr>
              <tr>
                <td>{"\\( \\Psi \\)"}</td>
                <td>
                  Ellipsometric amplitude ratio:{" "}
                  {"\\( \\tan(\\Psi) = |r_p / r_s| \\)"}
                </td>
                <td>degrees</td>
                <td>
                  Reflection, and transmission when {"\\( r \\)"} is subsituted
                  by {"\\( t \\)"}
                </td>
              </tr>
              <tr>
                <td>{"\\( \\Delta \\)"}</td>
                <td>
                  Ellipsometric phase shift:{" "}
                  {"\\( \\Delta = \\arg(r_p / r_s) \\)"}. Speqqle assumes{" "}
                  {"\\( \\Delta = 180^\\circ - \\Delta_\\text{measured} \\)"} by
                  default.
                </td>
                <td>degrees</td>
                <td>
                  Reflection, and transmission when {"\\( r \\)"} is subsituted
                  by {"\\( t \\)"}
                </td>
              </tr>
              <tr>
                <td>{"\\( \\alpha \\)"}</td>
                <td>
                  Amplitude ratio for analyzer modulation without compensator
                  (Fourier coefficient)
                </td>
                <td>unitless</td>
                <td>Reflection and transmission</td>
              </tr>
              <tr>
                <td>{"\\( \\beta \\)"}</td>
                <td>
                  Phase difference for analyzer modulation without retarder
                  (Fourier coefficient, depends on {"\\( \\Psi,\\ \\Delta \\)"})
                </td>
                <td>unitless</td>
                <td>Reflection and transmission</td>
              </tr>
            </tbody>
          </table>

          {/* Use Cases */}
          <h4>Use Cases</h4>
          <ul>
            <li>
              Extract dielectric functions or refractive indices from real-world
              spectra
            </li>
            <li>
              Determine thin-film or multilayer thicknesses non-destructively
            </li>
            <li>
              Analyze changes in surface coatings, oxide growth, or buried
              interfaces
            </li>
            <li>
              Study interference patterns and Fabry–Pérot resonances in optical
              stacks
            </li>
          </ul>

          {/* Setup Types */}
          <h4>Experiment Setup</h4>
          <ul>
            <li>
              <strong>Single-layer (bulk or thick slab):</strong>
              Use a single <strong>Material</strong> model with a large
              thickness (e.g., &gt; 1 - 10 cm or even more). You may choose to
              treat this layer as <strong>coherent</strong> (to capture
              Fabry-Pérot oscillations) or <strong>incoherent</strong> (to
              ignore internal interference). This setup is suitable for
              crystals, wafers, or thick films.
            </li>
            <li>
              <strong>Thin film on substrate:</strong>
              Define two layers: the thin film (coherent) and the substrate
              (often incoherent if thick). be sure to include experimental data
              of the substrate and the film on the substrate in your analysis.
              This setup is ideal for reflectivity and ellipsometry on coated
              samples.
            </li>
            <li>
              <strong>Multilayer stack:</strong>
              Add three or more layers (e.g., multiple coatings or
              heterostructures). Specify thickness and coherence for each.
              Interference between coherent layers produces Fabry-Pérot effects,
              while incoherent layers average out internal phases. Be sure to
              include the substrate in the optical stack.
            </li>
          </ul>

          {/* Warnings */}
          <MathJax>
            <h4>Points of Attention</h4>
            <ul>
              <li>
                <strong>Layer definitions:</strong> Ensure every layer includes
                a valid
                <strong> Material submodel </strong> and a realistic thickness
                value in cm.
              </li>
              <li>
                <strong>Coherence setting:</strong> Use the <code>coh</code>{" "}
                flag to indicate whether a layer is <strong>coherent</strong>{" "}
                (phase preserved) or <strong>incoherent</strong>
                (no phase memory). Coherent layers allow for Fabry–Pérot
                interference.
              </li>
              <li>
                <strong>Fabry–Pérot effects:</strong> Even a single slab of
                material can exhibit Fabry–Pérot oscillations if treated as
                coherent. Set <code>coh = 1</code> if you want to model internal
                interference.
              </li>
              <li>
                <strong>Angle and polarization:</strong> Make sure the angle of
                incidence
                {"\\( \\theta \\)"} and polarizer angle (&quot;pol&quot;) match
                your experimental setup. For somewhat curved samples, try
                fitting the angle.
              </li>
              <li>
                <strong>
                  Ellipsometric phase shift ({"\\( \\Delta \\)"}):
                </strong>{" "}
                Speqqle assumes ellipsometers <em>without</em> a compensator. It
                therefore uses:
                <br />
                <span style={{ marginLeft: "1em" }}>
                  {"\\( \\Delta = 180^\\circ - \\Delta_\\text{measured} \\)"}
                </span>
                <br />
                If your values appear inverted, adjust this during data import
                or preprocessing.
              </li>
              <li>
                <strong>Unit consistency:</strong> Use centimeters for thickness
                and {"\\( \\text{cm}^{-1} \\)"} for optical frequency{" "}
                {"\\( \\omega \\)"} to match Speqqle&apos;s internal unit
                system.
              </li>
            </ul>
          </MathJax>

          {/* Advanced Physics (Collapsible) */}
          <details style={{ marginTop: "1rem" }}>
            <summary>
              <strong>Advanced: Underlying Physics</strong>
            </summary>
            <p>
              Light propagation is computed using the transfer matrix method,
              solving Maxwell’s equations at every interface in the structure.
            </p>
            <p>
              Fresnel equations determine reflectance and transmittance at each
              boundary, including interference and polarization effects.
            </p>
            <p>
              For Ellipsometry:
              {"\\[ \\rho = \\frac{r_p}{r_s} = \\tan(\\Psi) e^{i\\Delta} \\]"}
            </p>
            <p>
              Speqqle internally uses the measured angle conversion:
              {"\\[ \\Delta = 180^\\circ - \\Delta_\\text{measured} \\]"}
            </p>
          </details>

          {/* Back to Top */}
          <p style={{ marginTop: "1.5rem" }}>
            <a href="#top">↑ Back to top</a>
          </p>

          <hr />
        </section>
      </MathJax>

      {/* Raman Section */}
      <section
        id="ramanmodel"
        style={{ marginTop: "3rem", marginBottom: "3rem" }}
      >
        <MathJax>
          <h3>Raman</h3>

          <p>
            This model fits and simulates{" "}
            <strong>Raman scattering spectra</strong>, including both
            <strong> Stokes</strong> and <strong>anti-Stokes</strong> sides. It
            allows for extraction of phonon mode energies and lifetimes and
            enables <strong>non-contact temperature estimation</strong> through
            Raman thermometry.
          </p>

          {/* Inputs Table */}
          <h4>Inputs</h4>
          <table className="manual-table">
            <thead>
              <tr>
                <th>Parameter</th>
                <th>Description</th>
                <th>Units</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{"\\( \\omega_0 \\)"}</td>
                <td>Resonance frequency of the phonon or vibrational mode</td>
                <td>
                  cm<sup>-1</sup>
                </td>
              </tr>
              <tr>
                <td>{"\\( A \\)"}</td>
                <td>Oscillator amplitude</td>
                <td>arb. units</td>
              </tr>
              <tr>
                <td>{"\\( \\gamma \\)"}</td>
                <td>Linewidth (FWHM) of the phonon mode</td>
                <td>
                  cm<sup>-1</sup>
                </td>
              </tr>

              <tr>
                <td>{"\\( Asymm \\)"}</td>
                <td>
                  Asymmetry parameter in the Breit Wigner Fano line shape.
                  Corresponds to {"\\( 1/q \\)"}. The excitation is symmatric
                  when Asymm = 0.
                </td>
                <td> - </td>
              </tr>
              <tr>
                <td>Laser frequency</td>
                <td>Frequency of excitation laser</td>
                <td>nm</td>
              </tr>
              <tr>
                <td> Constant corr, Lin corr, Quad corr</td>
                <td>
                  Camera correction coefficients: C( {"\\( \\omega \\)"})=
                  Constant corr + Lin corr {"\\( \\omega + \\)"} Quad corr{" "}
                  {"\\( \\omega^2 \\)"}. Keep Lin corr and Quad corr fixed to 0
                  unless you know what you are doing.
                </td>
                <td>arb. units</td>
              </tr>
              <tr>
                <td> abs coeff</td>
                <td>
                  frequency independent absorption correction for high-T thermal
                  background: {"\\( I \\propto \\)"} abs coeff{" "}
                  {"\\( B(\\omega, T) \\)"}, where {"\\( B(\\omega, T) \\)"} is
                  Planck&apos;s law
                </td>
                <td>varies</td>
              </tr>
            </tbody>
          </table>

          {/* Outputs Table */}
          <h4>Outputs</h4>
          <table className="manual-table">
            <thead>
              <tr>
                <th>Output</th>
                <th>Description</th>
                <th>Units</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Stokes spectrum</td>
                <td>Raman intensity for Stokes-shifted scattering</td>
                <td>arb. units</td>
              </tr>
              <tr>
                <td>Anti-Stokes spectrum</td>
                <td>Raman intensity for anti-Stokes scattering</td>
                <td>arb. units</td>
              </tr>
              <tr>
                <td>Fitted parameter: local temperature</td>
                <td>Extracted from Stokes/anti-Stokes ratio</td>
                <td>K</td>
              </tr>
              <tr>
                <td>
                  Other fitted parameters:{" "}
                  {"\\( \\omega_0 , A, \\gamma, Asymm \\)"}
                </td>
                <td>Extracted from Stokes and anti-Stokes ratio spectrum </td>
                <td>
                  cm<sup>-1</sup>, arb units, cm<sup>-1</sup>, arb units{" "}
                </td>
              </tr>
            </tbody>
          </table>

          {/* Use Cases */}
          <h4>Use Cases</h4>
          <ul>
            <li>
              Fit phonon peaks in Raman spectra using Breit-Wigner-Fano or
              Lorentz line shapes (For Asymm = 0)
            </li>
            <li>
              Extract vibrational frequencies and linewidths from bulk or
              thin-film Raman data
            </li>
            <li>
              Perform <strong>Raman thermometry</strong> by fitting the
              anti-Stokes to Stokes ratio
            </li>
            <li>
              Apply intensity corrections for detector response and thermal
              emission at high temperatures
            </li>
          </ul>

          {/* Setup */}
          <h4>Experiment Setup</h4>
          <ul>
            <li>
              <strong>Stokes-only:</strong> Standard Raman spectra with positive
              frequency shift; used for mode identification.
            </li>
            <li>
              <strong>Stokes + Anti-Stokes:</strong> Required for Raman
              thermometry. Ensure symmetry and background handling is accurate.
            </li>
            <li>
              <strong>High-temperature applications:</strong> Include camera and
              absorption corrections to avoid overestimating intensity.
            </li>
          </ul>

          {/* Points of Attention */}
          <h4>Points of Attention</h4>
          <ul>
            <li>
              Ensure input units (e.g., cm⁻¹ for frequencies) match throughout
              the model.
            </li>
            <li>
              Anti-Stokes intensity is sensitive to noise and background —
              proper baseline subtraction is essential.
            </li>
            <li>
              Use camera correction{" "}
              {"\\( C(\\omega) = a \\omega + b \\omega^2 \\)"} to account for
              detector sensitivity, but only if you really know what you are
              doing. We suggest keeping them fixed and zero.
            </li>
            <li>
              If thermal radiation dominates (e.g. at high T), enable absorption
              scaling:
              {
                "\\( I_{\\text{obs}} \\propto \\alpha(\\omega) B(\\omega, T) \\)"
              }
            </li>
            <li>
              For accurate temperature extraction, the system should be in
              thermal equilibrium and free from laser-induced heating artifacts.
            </li>
          </ul>

          {/* Physics Block */}
          <details style={{ marginTop: "1rem" }}>
            <summary>
              <strong>Advanced: Underlying Physics</strong>
            </summary>
            <p>
              Raman scattering occurs when photons interact with vibrational
              modes. The scattered light is shifted in energy by{" "}
              {"\\( \\pm \\hbar\\omega \\)"}:
            </p>
            <p style={{ marginLeft: "1em" }}>
              {
                "\\[ E_{\\text{scattered}} = E_{\\text{laser}} \\pm \\hbar\\omega \\]"
              }
            </p>
            <p>The phonon population follows the Bose-Einstein distribution:</p>
            <p style={{ marginLeft: "1em" }}>
              {"\\[ n(\\omega) = \\frac{1}{e^{\\hbar\\omega / k_B T} - 1} \\]"}
            </p>
            <p>The resulting Raman intensity is:</p>
            <ul>
              <li>
                Stokes: {"\\( I \\propto [n(\\omega) + 1] \\chi''(\\omega) \\)"}
              </li>
              <li>
                Anti-Stokes: {"\\( I \\propto n(\\omega) \\chi''(\\omega) \\)"}
              </li>
            </ul>
            <p>Temperature can be extracted from the intensity ratio:</p>
            <p style={{ marginLeft: "1em" }}>
              {
                "\\[ \\frac{I_{AS}}{I_S} = e^{-\\hbar\\omega / k_B T} \\quad \\Rightarrow \\quad T = \\frac{\\hbar\\omega}{k_B \\ln(I_S / I_{AS})} \\]"
              }
            </p>
            <p>The model optionally applies:</p>
            <ul>
              <li>
                Camera correction: C( {"\\( \\omega \\)"})= Constant corr + Lin
                corr {"\\( \\omega + \\)"} Quad corr {"\\( \\omega^2 \\)"}
              </li>
              <li>
                Thermal correction: {"\\( I \\propto \\)"} abs coeff{" "}
                {"\\( B(\\omega, T) \\)"}
              </li>
            </ul>
            <p>
              These enable realistic simulation and analysis, especially in
              high-temperature or low-signal environments.
            </p>
          </details>

          {/* Back to Top */}
          <p style={{ marginTop: "1.5rem" }}>
            <a href="#top">↑ Back to top</a>
          </p>

          <hr />
        </MathJax>
      </section>


     {/* Point Dipole Section - Updated to match manual style */}
     <section
        id="PDmodelbulk"
        style={{ marginTop: "3rem", marginBottom: "3rem" }}
      >
        <MathJax>
          <h3>SNOM: Bulk Point Dipole Model</h3>

          <p>
            This model simulates and fits near-field optical measurements performed on
            bulk samples using a scattering-type scanning near-field optical
            microscope (s-SNOM). It models the interaction between the tip and
            the sample using a point dipole approximation. The output includes
            signal amplitude and phase as measured by a typical SNOM setup and normalized to a perfect reference.
          </p>

          <h4>Inputs</h4>
          <table className="manual-table">
            <thead>
              <tr>
                <th>Parameter</th>
                <th>Description</th>
                <th>Units</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{"\\( a \\)"}</td>
                <td>Tip radius (radius of the point dipole)</td>
                <td>nm</td>
              </tr>
              <tr>
                <td>{"\\( z_0 \\)"}</td>
                <td>Minimal tip-to-sample distance</td>
                <td>nm</td>
              </tr>
              <tr>
                <td>{"\\( z_{amp} \\)"}</td>
                <td>Tapping amplitude of the oscillating tip</td>
                <td>nm</td>
              </tr>
              <tr>
                <td>{"\\( N_{harm} \\)"}</td>
                <td>Number of harmonics used in the signal calculation</td>
                <td> integer</td>
              </tr>
              <tr>
                <td>{"\\( N_{harm}^{High} \\)"}</td>
                <td>Number of a higher harmonics used in the signal calculation when a higher Harmonic is used as a reference signal</td>
                <td> integer</td>
              </tr>
              <tr>
                <td>{"\\( NFT \\)"}</td>
                <td>Number of Fourier transform samples for tip modulation</td>
                <td> integer</td>
              </tr>
              <tr>
                <td>{"\\( FF   cor \\)"}</td>
                <td>Far-field correction on or off (unchecked: disabled)</td>
                <td>-</td>
              </tr>
              <tr>
                <td>{"\\( FF   coef \\)"}</td>
                <td>Far-field correction coefficient (0 disables it)</td>
                <td>-</td>
              </tr>
              <tr>
                <td>{"\\( \\alpha \\)"}</td>
                <td>Angle of incidence for the far-field correction</td>
                <td>degrees</td>
              </tr>
              <tr>
                <td>Material Model</td>
                <td>Linked dielectric function of the bulk sample, usually Drude-Lorentz</td>
                <td>-</td>
              </tr>
            </tbody>
          </table>

          <h4>Outputs</h4>
          <table className="manual-table">
            <thead>
              <tr>
                <th>Output</th>
                <th>Description</th>
                <th>Units</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{"\\( S(\\omega) \\)"}</td>
                <td>Near-field amplitude signal of the {"\\( N_{Harm} \\)"} Harmonic</td>
                <td>arb. units</td>
              </tr>
              <tr>
                <td>{"\\( P(\\omega) \\)"}</td>
                <td>Near-field phase signal</td>
                <td>degrees or radians</td>
              </tr>
              <tr>
              <td>{"\\( S(\\omega) \\)"}</td>
                <td>Near-field amplitude signal of the {"\\(  N_{Harm} \\)"} Harmonic</td>
                <td>arb. units</td>
              </tr>
            </tbody>
          </table>

          <h4>Use Cases</h4>
          <ul>
            <li>Fitting near-field reflectivity or contrast data in s-SNOM</li>
            <li>Validating far-field dielectric functions using near-field data</li>
            <li>Quantifying dielectric properties at the nanoscale</li>
          </ul>

          <h4>Points of Attention</h4>
          <ul>
            <li>
              Always link a valid material model. This model calculates its
              output based on the material dielectric function
              {"\\( \\varepsilon(\\omega) \\)"}.
            </li>
            <li>
              To disable the far-field correction, set the correction coefficient
              to {"\\( FF coef = 0 \\)"} or uncheck the FF cor check-box.
            </li>
            <li>
              The number of FFT points and harmonics must be sufficiently high to
              avoid aliasing artifacts.
            </li>
            <li>
              The tip radius and tapping amplitude significantly influence the
              signal&apos;s depth sensitivity, always set them to the known values of the experiment and avoid fitting them in a first approach.
            </li>
          </ul>

          <details style={{ marginTop: "1rem" }}>
            <summary>
              <strong>Advanced: Underlying Physics</strong>
            </summary>
            <p>
              The model solves a simplified dipole–sample interaction, assuming a
              vertically modulated tip and evaluating the time-dependent
              reflection coefficient using Fourier transforms.
            </p>
            <MathJax>
              {`
              \\[
                \\beta = \\frac{\\varepsilon - 1}{\\varepsilon + 1}, \\quad
                \\alpha(z) = \\frac{a^3}{1 - \\beta \\cdot \\frac{a^3}{4z^3}}, \\quad
                S(\\omega) = \\left| \\sum_n \\alpha(z_n) e^{i n \\phi_n} \\right|, \\quad
                P(\\omega) = \\arg\\left( \\sum_n \\alpha(z_n) e^{i n \\phi_n} \\right)
              \\]
              `}
            </MathJax>
            <p>
              where {"\\( z_n = z_0 + a + z_{amp}(1 + \\cos \\phi_n) \\)"} varies
              during tapping. The signal is obtained from the nth harmonic of the
              time-domain dipole response.
            </p>
          </details>

          <p style={{ marginTop: "1.5rem" }}>
            <a href="#top">↑ Back to top</a>
          </p>

          <hr />
        </MathJax>
      </section>


      <section
  id="FDmodelbulk"
  style={{ marginTop: "3rem", marginBottom: "3rem" }}
>
  <MathJax>
    <h3>SNOM: Bulk Finite Dipole Model</h3>

    <p>
      This model simulates and fits near-field optical signals from bulk
      samples using a finite dipole approximation, as originally proposed
      by Lewin. It improves upon the point dipole model by better
      representing the elongated geometry of the SNOM tip and the
      resulting electrostatic field distributions. This is particularly
      relevant for quantitative analysis of scattering-type near-field
      microscopy (s-SNOM) experiments.
    </p>

    <h4>Inputs</h4>
    <table className="manual-table">
      <thead>
        <tr>
          <th>Parameter</th>
          <th>Description</th>
          <th>Units</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>{"\\( a \\)"}</td>
          <td>Tip radius of the dipole</td>
          <td>nm</td>
        </tr>
        <tr>
          <td>{"\\( z_0 \\)"}</td>
          <td>Minimal tip–sample distance</td>
          <td>nm</td>
        </tr>
        <tr>
          <td>{"\\( z_{amp} \\)"}</td>
          <td>Tapping amplitude of the oscillating tip</td>
          <td>nm</td>
        </tr>
        <tr>
          <td>{"\\( L \\)"}</td>
          <td>Effective tip length (default: 600 nm)</td>
          <td>nm</td>
        </tr>
        <tr>
          <td>{"\\( N_{harm} \\)"}</td>
          <td>Harmonic used for signal calculation</td>
          <td>integer</td>
        </tr>
        <tr>
        <td>{"\\( N_{harm}^{High} \\)"}</td>
          <td>the higher Harmonic used for signal calculation</td>
          <td>integer</td>
        </tr>
        <tr>
          <td>{"\\( NFT \\)"}</td>
          <td>Fourier transform sampling points</td>
          <td>integer</td>
        </tr>
        <tr>
          <td>{"\\( FF\\ cor \\)"}</td>
          <td>Enable or disable far-field correction (checkbox)</td>
          <td>-</td>
        </tr>
        <tr>
          <td>{"\\( FF\\ coef \\)"}</td>
          <td>Far-field correction coefficient</td>
          <td>-</td>
        </tr>
        <tr>
          <td>{"\\( \\alpha \\)"}</td>
          <td>Angle of incidence</td>
          <td>degrees</td>
        </tr>
        <tr>
          <td>Material Model</td>
          <td>Linked bulk dielectric function (e.g. Drude–Lorentz)</td>
          <td>-</td>
        </tr>
      </tbody>
    </table>

    <h4>Outputs</h4>
<table className="manual-table">
  <thead>
    <tr>
      <th>Output</th>
      <th>Description</th>
      <th>Units</th>
    </tr>
  </thead>
  <tbody>
    <tr>
      <td>{"\\( S_{Harm}(\\omega) \\)"}</td>
      <td>Amplitude normalized to the reference at {"\\( N_{Harm} \\)"} </td>
      <td> - </td>
    </tr>
    <tr>
      <td>{"\\( P_{Harm}(\\omega) \\)"}</td>
      <td>Phase normalized to the reference at {"\\( N_{Harm} \\)"}</td>
      <td>radians</td>
    </tr>
    <tr>
      <td>{"\\( S(\\omega)^{High Harmonic}/S(\\omega)^{Harm} \\)"}</td>
      <td>Amplitude normalized to the Amplitude of the {"\\( N_{Harm} \\)"} signal. Here there is no prior normalization to the reference signal for both {"\\( S(\\omega) \\)"}</td>
      <td> - </td>
    </tr>
    <tr>
      <td>{"\\( P(\\omega)^{High Harmonic}/P(\\omega)^{Harm} \\)"}</td>
      <td>Phase normalized to the Phase of the {"\\( N_{Harm} \\)"} signal. Here there is no prior normalization to the reference signal for both {"\\( P(\\omega) \\)"}</td>
      <td>radians</td>
    </tr>
    <tr>
      <td>{"\\( S(\\omega)^{High Harmonic} \\)"}</td>
      <td>Amplitude normalized to the reference, both at {"\\( N_{Harm}^{High} \\)"}</td>
      <td> - </td>
    </tr>
    <tr>
      <td>{"\\( P(\\omega)^{High Harmonic} \\)"}</td>
      <td>Phase normalized to the reference, both at {"\\( N_{Harm}^{High} \\)"}</td>
      <td>radians</td>
    </tr>
    <tr>
      <td>{"\\( \\text{Re}[\\eta_{sam}(\\omega) / \\eta_{ref}(\\omega)] \\)"}</td>
      <td>Real part of the complex signal normalized to the reference for {"\\( N_{Harm} \\)"}</td>
      <td>arb. units</td>
    </tr>
    <tr>
      <td>{"\\( \\text{Im}[\\eta_{sam}(\\omega) / \\eta_{ref}(\\omega) ] \\)"}</td>
      <td>Imaginary part of the complex signal normalized to the reference for {"\\( N_{Harm} \\)"}</td>
      <td> - </td>
    </tr>
    <tr>
      <td>{"\\( \\text{Abs}[\\eta_{sam}(\\omega)] \\)"}</td>
      <td>Amplitude of the sample signal for {"\\( N_{Harm} \\)"}, not normalized to the reference</td>
      <td> - </td>
    </tr>
    <tr>
      <td>{"\\( \\text{Arg}[\\eta_{sam}(\\omega)] \\)"}</td>
      <td>Phase of the signal for {"\\( N_{Harm} \\)"}, not normalized by the reference</td>
      <td>radians</td>
    </tr>
    </tbody>
</table>


    <h4>Use Cases</h4>
    <ul>
      <li>
        Simulating and fitting s-SNOM spectra with improved tip–sample
        interaction physics taking the dielectric properties of the bulk sample into account. 
      </li>
      <li>
        Quantitative extraction of dielectric properties from near-field
        data.
      </li>
      <li>
        Cross-validation of far-field and near-field optical constants.
      </li>
    </ul>

    <h4>Points of Attention</h4>
    <ul>
      <li>
        Always use a well-constructed dielectric model for the material.
      </li>
      <li>
        If the far-field correction is not needed, disable it using the
        checkbox or set the correction coefficient to zero.
      </li>
      <li>
        Ensure the tip radius and tapping amplitude are set realistically
        from your experiment to start with. We have noticed that fitting the tip radius can improve results significantly and concluded that the tip is often more blunt than expected. 
      </li>
      <li>
        Do not overfit geometric parameters unless you have an
        experimental reason.
      </li>
      <li>
  The tip length <MathJax inline>{"\\( L \\)"}</MathJax> is typically fixed to a realistic value (e.g., 600 nm) to represent the effective height of the SNOM tip. This length determines the spacing between the two image dipoles used in the finite dipole approximation. If <MathJax inline>{"\\( L \\)"}</MathJax> is set too large, the two dipoles become too widely separated, which artificially enhances far-field contributions and distorts the near-field signal. This can lead to an overestimation of background effects and reduce spatial resolution. Fit <MathJax inline>{"\\( L \\)"}</MathJax> only with care, and constrain it to values consistent with the physical tip geometry.
</li>
    </ul>

    <details style={{ marginTop: "1rem" }}>
      <summary>
        <strong>Advanced: Underlying Physics</strong>
      </summary>
      <p>
        The finite dipole model uses the Lewin approach to approximate the
        effective polarizability of the tip in proximity to the sample
        surface. It captures retardation effects and lateral field
        distributions more accurately than point dipole models.
      </p>
      <MathJax>
        {`
        \\[
        Z(\\phi) = z_0 + z_{amp} (1 + \\cos \\phi)
        \\]`}
      </MathJax>
      <p>
        The effective polarizability{" "}
        {"\\( \\eta(\\omega) \\)"} is calculated using:
      </p>
      <MathJax>
        {`
        \\[
        \\eta(\\phi, \\beta) =
        \\frac{\\beta (2 L g - 2 Z - W_0 - a) \\ln \\left( \\frac{4L}{4Z + 2 W_0 + R} \\right)}{4L \\ln(4L/R) - \\beta (4 L g - 4 Z - 3a) \\ln \\left( \\frac{4L}{4Z + 2 R} \\right)}
        \\]`}
      </MathJax>
      <p>
        Here{" "}
        {"\\( \\beta = \\frac{\\varepsilon - 1}{\\varepsilon + 1} \\)"} and{" "}
        {"\\( W_0 = 1.31 a \\)"}, with {"\\( g = 0.7 e^{0.06i} \\)"}.
      </p>
      <p>
        The signal is then constructed from the harmonic component of the
        complex-valued near-field response:
      </p>
      <MathJax>
        {`
        \\[
        S(\\omega) = \\left| 
        \\frac{
          \\eta_{sam} (1 + C_f e^{-i 2 \\pi \\omega L \\cos \\alpha} r_p^{sam})
        }{
          \\eta_{ref} (1 + C_f e^{-i 2 \\pi \\omega L \\cos \\alpha} r_p^{ref})
        }
        \\right|
        \\quad , \\quad
        P(\\omega) = \\arg(\\dots)
        \\]`}
      </MathJax>
      <p>
        The reflection coefficients are computed using angle-dependent
        Fresnel expressions:
      </p>
      <MathJax>
        {`
        \\[
        r_p(\\varepsilon, \\alpha) = 
        \\frac{\\varepsilon \\cos \\alpha - \\sqrt{\\varepsilon - \\sin^2 \\alpha}}{
          \\varepsilon \\cos \\alpha + \\sqrt{\\varepsilon - \\sin^2 \\alpha}
        }
        \\]`}
      </MathJax>
    </details>

    <p style={{ marginTop: "1.5rem" }}>
      <a href="#top">↑ Back to top</a>
    </p>
    <hr />
  </MathJax>
</section>


      <h2>What If the Experiment You Need Is Not Listed?</h2>
      <p>If the experiment you need is not yet included, you can:</p>
      <ul>
        <li>
          Check if it&apos;s available in a{" "}
          <Link to="../packages/overview">special package</Link>.
        </li>
        <li>
          Request it via the <strong>Feedback Form</strong> in your{" "}
          Profile.
        </li>
        <li>Email the Speqqle team with your use case.</li>
      </ul>

      <h2>Next Steps</h2>
      <ul>
        <li>
        <Link to="../experiments/loadmodels">Load a saved experiment or submodel</Link>.
        </li>
        <li>
        <Link to="../experiments/materialmodels">Edit or extend your material model</Link>.
        </li>
        <li>
        <Link to="../parameters/editing">Edit or extend your material model</Link>.
        </li>
        <li>
        <Link to="../graphs/overview">Plot your simulated or fitted dat</Link>.
        </li>
      </ul>
    </ManualContent>
  );
};

export default ModelList;
